import { computed, ref } from 'vue';
import { DialogQueuePriority } from '@/core/dialogsystem/dialogQueue';
import { useDialog } from '@/core/dialogsystem/useDialog';
import FirstEntrySuccessDialog from '@/core/dialogs/first-entry-success/FirstEntrySuccessDialog.vue';
import { useGrowthCta } from './useGrowthCta';

export function useFirstEntrySuccessDialog() {
  const { canShowGrowthPopups } = useGrowthCta();
  const showTeamInvite = ref(false);
  const isSimpleSuccess = ref(false);
  const onInviteTeam = ref<(() => Promise<void> | void) | undefined>(undefined);
  const onPrimary = ref<(() => Promise<void> | void) | undefined>(undefined);
  const onSecondary = ref<(() => Promise<void> | void) | undefined>(undefined);
  const onDismiss = ref<(() => Promise<void> | void) | undefined>(undefined);
  const primaryLabel = ref<string | undefined>(undefined);
  const secondaryLabel = ref<string | undefined>(undefined);
  const hintText = ref<string | undefined>(undefined);
  const onClose = ref<(() => void) | undefined>(undefined);

  const dialog = useDialog({
    component: FirstEntrySuccessDialog,
    props: computed(() => ({
      showTeamInvite: showTeamInvite.value,
      isSimpleSuccess: isSimpleSuccess.value,
      onInviteTeam: onInviteTeam.value,
      onPrimary: onPrimary.value,
      onSecondary: onSecondary.value,
      onDismiss: onDismiss.value,
      primaryLabel: primaryLabel.value,
      secondaryLabel: secondaryLabel.value,
      hintText: hintText.value,
      onClose: onClose.value,
    })),
  });

  const show = async (params: {
    showTeamInvite: boolean;
    isSimpleSuccess?: boolean;
    onInviteTeam?: () => Promise<void> | void;
    onPrimary?: () => Promise<void> | void;
    onSecondary?: () => Promise<void> | void;
    onDismiss?: () => Promise<void> | void;
    primaryLabel?: string;
    secondaryLabel?: string;
    hintText?: string;
    onClose?: () => void;
  }): Promise<void> => {
    showTeamInvite.value = params.showTeamInvite;
    isSimpleSuccess.value = params.isSimpleSuccess ?? false;
    onInviteTeam.value = params.onInviteTeam;
    onPrimary.value = params.onPrimary;
    onSecondary.value = params.onSecondary;
    onDismiss.value = params.onDismiss;
    primaryLabel.value = params.primaryLabel;
    secondaryLabel.value = params.secondaryLabel;
    hintText.value = params.hintText;
    onClose.value = params.onClose;
    await dialog.showQueued({
      dedupeKey: 'growth:first-entry-success',
      guard: () => canShowGrowthPopups.value,
      onSkipped: params.onClose,
      priority: DialogQueuePriority.OwnerActivation,
    });
  };

  return {
    show,
  };
}
